import { useMemo, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { QueryState } from '@/components/QueryState';
import {
  useCreateSlip,
  useDeleteSlip,
  useFieldGrants,
  useMaterialOptions,
  useMaterialSlips,
  type SlipKind,
} from '@/lib/field';
import { shortDate, today } from '@/lib/format';
import { shrinkImage } from '@/lib/image';
import { useSite } from '@/lib/useSite';

/** 웹 자재 탭의 키 — 전표 등록은 CREATE, 지우기는 EDIT 다(`MaterialSlipController`). */
const MATERIAL_MENU = 'site_pmis_material';

/** 「미지정」 — 층을 모를 때 사람이 고르는 줄. 서버로는 null 로 간다(짐작해 앉히지 않는다). */
const ZONE_NONE = '__none__';

const KIND_LABEL: Record<SlipKind, string> = { DELIVERED: '반입', USED: '사용' };

/** 이 달 1일 — 목록은 이 달 것부터 본다. */
function firstOfMonth(): string {
  return `${today().slice(0, 8)}01`;
}

/**
 * 자재 반입·사용 전표 (DOC-90 · 앱 `MaterialSlipScreen` 과 같은 칸).
 *
 * <p>필수는 날짜·자재·사용위치(층) 셋이다(사용자 지시 2026-09-21). 층을 모르면 「미지정」 을 <b>고르게</b>
 * 한다 — 빈 칸으로 흘려보내는 것과 사람이 모른다고 고른 것은 다른 일이다. 송장은 줄이 선 뒤에 붙는다.
 *
 * <p>쓰기는 웹에서 받은 권한 그대로다 — 등록은 자재 메뉴 등록 권한, 지우기는 수정 권한.
 */
export function MaterialSlipsPage() {
  const { siteId, siteName, readOnly } = useSite();
  const { can } = useFieldGrants();
  const canCreate = can(MATERIAL_MENU, 'create') && !readOnly;
  const canEdit = can(MATERIAL_MENU, 'edit') && !readOnly;

  const from = useMemo(firstOfMonth, []);
  const to = useMemo(today, []);
  const slips = useMaterialSlips(siteId, from, to);
  const remove = useDeleteSlip(siteId);
  const [open, setOpen] = useState(false);
  const rows = slips.data ?? [];

  return (
    <>
      <PageHeader
        title="자재 전표"
        subtitle={siteName}
        action={
          canCreate && (
            <button type="button" className="btn-primary px-3 py-1.5 text-ui-xs" onClick={() => setOpen(true)}>
              등록
            </button>
          )
        }
      />

      {open && <SlipForm siteId={siteId} onDone={() => setOpen(false)} />}

      <QueryState pending={slips.isPending} error={slips.isError} empty={rows.length === 0} emptyText="이 달 전표가 없습니다.">
        <ul className="flex flex-col gap-2">
          {rows.map((row) => (
            <li key={row.id} className="card">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate text-ui-md">
                    <span className="font-medium">{KIND_LABEL[row.slipKind] ?? row.slipKind}</span> · {row.materialName ?? '-'}
                    {row.spec ? ` ${row.spec}` : ''}
                  </div>
                  <div className="truncate text-ui-2xs" style={{ color: 'var(--text-muted)' }}>
                    {shortDate(row.slipDate)} · {[row.buildingName, row.zoneName ?? '미지정'].filter(Boolean).join(' ')}
                    {row.locationNote ? ` · ${row.locationNote}` : ''}
                    {row.vendorName ? ` · ${row.vendorName}` : ''}
                    {row.createdByName ? ` · ${row.createdByName}` : ''}
                    {row.fileCount > 0 ? ` · 송장 ${row.fileCount}` : ''}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-ui-lg font-semibold">
                    {row.quantity !== null ? row.quantity.toLocaleString('ko-KR') : '-'}
                    {row.unit ? <span className="ml-0.5 text-ui-xs font-normal">{row.unit}</span> : null}
                  </div>
                  {canEdit && (
                    <button
                      type="button"
                      className="btn-secondary mt-1 px-2 py-1 text-ui-2xs"
                      disabled={remove.isPending}
                      onClick={() => {
                        if (window.confirm('이 전표를 지울까요?')) remove.mutate(row.id);
                      }}
                    >
                      지우기
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </QueryState>
    </>
  );
}

function SlipForm({ siteId, onDone }: { siteId: string; onDone: () => void }) {
  const options = useMaterialOptions(siteId, true);
  const create = useCreateSlip(siteId);
  const picker = useRef<HTMLInputElement>(null);

  const [slipDate, setSlipDate] = useState(today);
  const [slipKind, setSlipKind] = useState<SlipKind>('DELIVERED');
  const [materialId, setMaterialId] = useState('');
  const [buildingId, setBuildingId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [locationNote, setLocationNote] = useState('');
  const [quantity, setQuantity] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [note, setNote] = useState('');
  const [invoice, setInvoice] = useState<File | null>(null);

  const items = options.data?.items ?? [];
  const buildings = options.data?.buildings ?? [];
  // 동을 고르면 그 동의 층만 남는다. 안 고르면 전부 — 층 이름만 아는 사람을 막지 않는다.
  const zones = (options.data?.zones ?? []).filter((zone) => buildingId === '' || zone.buildingId === buildingId);
  const buildingName = new Map(buildings.map((one) => [one.id, one.name]));
  const unit = items.find((item) => item.id === materialId)?.unit ?? null;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const amount = Number(quantity.replace(/[^0-9.]/g, ''));
    await create.mutateAsync({
      body: {
        slipDate,
        materialId,
        zoneId: zoneId === ZONE_NONE ? null : zoneId,
        locationNote: locationNote.trim() || null,
        slipKind,
        quantity: amount,
        vendorName: vendorName.trim() || null,
        note: note.trim() || null,
      },
      invoice,
    });
    onDone();
  }

  return (
    <form onSubmit={onSubmit} className="card mb-3">
      <div className="mb-3 flex gap-2">
        <label className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>날짜</span>
          <input type="date" className="input-field" value={slipDate} onChange={(e) => setSlipDate(e.target.value)} required />
        </label>
        <div className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>구분</span>
          <div className="flex gap-2">
            {(['DELIVERED', 'USED'] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                className={`${slipKind === kind ? 'btn-primary' : 'btn-secondary'} flex-1`}
                onClick={() => setSlipKind(kind)}
              >
                {KIND_LABEL[kind]}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="mb-3 block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>자재</span>
        <select className="input-field" value={materialId} onChange={(e) => setMaterialId(e.target.value)} required>
          <option value="">선택하세요</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.spec ? `${item.name} · ${item.spec}` : item.name}
            </option>
          ))}
        </select>
      </label>

      <div className="mb-3 flex gap-2">
        <label className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>동</span>
          <select
            className="input-field"
            value={buildingId}
            onChange={(e) => {
              setBuildingId(e.target.value);
              setZoneId('');
            }}
          >
            <option value="">동 전체</option>
            {buildings.map((one) => (
              <option key={one.id} value={one.id}>{one.name}</option>
            ))}
          </select>
        </label>
        <label className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>층</span>
          <select className="input-field" value={zoneId} onChange={(e) => setZoneId(e.target.value)} required>
            <option value="">선택하세요</option>
            {zones.map((zone) => (
              <option key={zone.id} value={zone.id}>
                {buildingId === '' && zone.buildingId !== null && buildingName.has(zone.buildingId)
                  ? `${buildingName.get(zone.buildingId)} ${zone.zoneName}`
                  : zone.zoneName}
              </option>
            ))}
            <option value={ZONE_NONE}>미지정</option>
          </select>
        </label>
      </div>

      <label className="mb-3 block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>위치</span>
        <input type="text" className="input-field" value={locationNote} onChange={(e) => setLocationNote(e.target.value)} />
      </label>

      <div className="mb-3 flex gap-2">
        <label className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>
            {unit ? `수량 (${unit})` : '수량'}
          </span>
          <input
            type="text"
            inputMode="decimal"
            className="input-field"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            required
          />
        </label>
        <label className="flex-1">
          <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>납품처</span>
          <input type="text" className="input-field" value={vendorName} onChange={(e) => setVendorName(e.target.value)} />
        </label>
      </div>

      <label className="mb-3 block">
        <span className="mb-1 block text-ui-xs" style={{ color: 'var(--text-secondary)' }}>비고</span>
        <input type="text" className="input-field" value={note} onChange={(e) => setNote(e.target.value)} />
      </label>

      <div className="mb-4">
        <input
          ref={picker}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) setInvoice(await shrinkImage(file));
          }}
        />
        <button type="button" className="btn-secondary w-full" onClick={() => picker.current?.click()}>
          {invoice !== null ? invoice.name : '송장 사진'}
        </button>
      </div>

      {create.isError && (
        <p className="mb-3 text-ui-xs" style={{ color: 'var(--danger)' }}>저장하지 못했습니다.</p>
      )}

      <div className="flex gap-2">
        <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
          {create.isPending ? '보내는 중입니다…' : '등록'}
        </button>
        <button type="button" className="btn-secondary" onClick={onDone}>취소</button>
      </div>
    </form>
  );
}
